import { CsvIcon, ExportPageIcon } from "../../assets/icons";
import { AppRoutes } from "../../common/routesUtils";
import { PageType } from "../page.types";
import { DynamicLayout } from "./headerPage.types";
import {
	HeaderPageContextMap,
	OperationalUnitsHeaderPageContext,
	TransportGreenRouterDetailHeaderPageContext,
	TransportHeaderPageContext,
} from "./headerPageMap.dynamic";

/**
 * Dynamic header layout logic
 * ----------------------------
 * In addition to static configuration (breadcrumbs, filters),
 * some pages require dynamic header actions
 *
 * This function builds that dynamic portion based on:
 * 1. `pageType` – identifies which page is currently rendered.
 * 2. `context`  – page-specific runtime data (payload, loading state, handlers).
 *
 * The HeaderPageContextMap guarantees type safety by mapping each
 * PageType to its expected context shape.
 *
 * Flow:
 * - The View passes `pageType` and an optional `headerContext`.
 * - If the page requires dynamic behavior
 *   we use its typed context to generate actions (download, export, etc.).
 * - If no dynamic behavior is needed, we return `undefined`.
 *
 */

export function buildDynamicLayout(
	pageType: keyof HeaderPageContextMap,
	context?: HeaderPageContextMap[keyof HeaderPageContextMap] // optional
): DynamicLayout | undefined {
	switch (pageType) {
		case PageType.ResultsTransport: {
			// required for transport
			if (!context) return undefined;

			const { payload, fileDownload } = context as TransportHeaderPageContext;

			return {
				pageActions: [
					{
						label: "common:export",
						action: { key: "download" },
						loading: fileDownload.pdfTransport.isLoading,
						disabled: false,
						menuItems: [
							{
								label: "common:download CSV",
								icon: <CsvIcon />,
								onClick: () => console.log("CSV downloaded"),
								disabled: true,
							},
							{
								label: "common:download transport report",
								icon: <ExportPageIcon />,
								onClick: () => fileDownload.pdfTransport.postPdf(payload, "transport-report.pdf"),
								disabled: false,
							},
						],
					},
				],
			};
		}

		case PageType.ResultsOperationalUnits: {
			// required for transport
			if (!context) return undefined;

			const { payload, fileDownload } = context as OperationalUnitsHeaderPageContext;

			return {
				pageActions: [
					{
						label: "common:export",
						action: { key: "download" },
						loading: fileDownload.pdfOperationalUnits.isLoading,
						disabled: false,
						menuItems: [
							{
								label: "common:download CSV",
								icon: <CsvIcon />,
								onClick: () =>
									fileDownload.csvOperationalUnits.postPdf(payload, "operational-units-report.csv"),
								disabled: false,
							},
							{
								label: "common:download operational units report",
								icon: <ExportPageIcon />,
								onClick: () =>
									fileDownload.pdfOperationalUnits.postPdf(payload, "operational-units-report.pdf"),
								disabled: false,
							},
						],
					},
				],
			};
		}

		case PageType.ResultsTransportGreenRouterDetail: {
			if (!context) return undefined;

			const { refresh, loading } = context as TransportGreenRouterDetailHeaderPageContext;
			return {
				pageActions: [
					{
						label: "common:reload",
						action: {
							key: "refresh",
							function: refresh,
						},
						loading,
						disabled: loading,
					},
					{
						label: "common:export",
						action: { key: "download" },
						disabled: false,
						menuItems: [
							{
								label: "common:download CSV",
								icon: <CsvIcon />,
								onClick: () => console.log("CSV downloaded"),
								disabled: true,
							},
							{
								label: "common:download PDF",
								icon: <ExportPageIcon />,
								onClick: () => console.log("PDF downloaded"),
								disabled: true,
							},
						],
					},
				],
			};
		}

		case PageType.ResultsTransportGreenRouterEntityDetail: {
			if (!context) return undefined;

			const { refresh, loading } = context as TransportGreenRouterDetailHeaderPageContext;
			return {
				pageActions: [
					{
						label: "common:reload",
						action: {
							key: "refresh",
							function: refresh,
						},
						loading,
						disabled: loading,
					},
				],
			};
		}

		case PageType.ResultsOperationalUnitsGreenRouterDetail: {
			if (!context) return undefined;

			const { refresh, loading } = context as TransportGreenRouterDetailHeaderPageContext;

			return {
				pageActions: [
					{
						label: "common:reload",
						action: {
							key: "refresh",
							function: refresh,
						},
						loading,
						disabled: loading,
					},
					{
						label: "common:export",
						action: { key: "download" },
						disabled: false,
						menuItems: [
							{
								label: "common:download CSV",
								icon: <CsvIcon />,
								onClick: () => console.log("CSV downloaded"),
								disabled: true,
							},
							{
								label: "common:download operational units report",
								icon: <ExportPageIcon />,
								onClick: () => console.log("PDF downloaded"),
								disabled: true,
							},
						],
					},
				],
			};
		}

		case PageType.MasterDataOperationalUnits: {
			if (!context) return undefined;

			const { navigate, visitPage, openModal, isAddForbidden } =
				context as HeaderPageContextMap[PageType.MasterDataOperationalUnits];

			return {
				pageActions: [
					{
						label: "common:add operating unit",
						action: {
							key: "add",
							function: () => {
								navigate(AppRoutes.MasterDataOperationalUnitsAddUnit);
								visitPage(PageType.MasterDataOperationalUnitsAddUnit);
							},
						},
						disabled: false,
						forbidden: isAddForbidden,
					},
					{
						label: "common:import",
						action: {
							key: "open",
							function: () => {
								openModal();
							},
						},
						disabled: true,
					},
				],
			};
		}

		case PageType.MasterDataOperationalUnitsDetail: {
			if (!context) return undefined;

			const { linkToPage, loading } = context as HeaderPageContextMap[PageType.MasterDataOperationalUnitsDetail];

			if (!linkToPage) return undefined;

			return {
				pageActions: [
					{
						label: "common:see in the results",
						loading,
						action: {
							key: "link",
							function: linkToPage,
						},
						disabled: false,
					},
				],
			};
		}

		case PageType.DataInsightsRootCause: {
			if (!context) return undefined;

			const { runAnalysis, disabled, forbidden } =
				context as HeaderPageContextMap[PageType.DataInsightsRootCause];

			return {
				pageActions: [
					{
						label: "common:run analysis",
						action: {
							key: "linkRootCause",
							function: runAnalysis,
						},
						disabled,
						forbidden,
					},
				],
			};
		}

		case PageType.DataInsightsRootCauseResults: {
			if (!context) return undefined;

			const { editRedirect, newAnalysisRedirect } =
				context as HeaderPageContextMap[PageType.DataInsightsRootCauseResults];

			return {
				pageActions: [
					{
						label: "common:edit",
						action: {
							key: "edit",
							function: editRedirect,
						},
						disabled: false,
					},
					{
						label: "common:export",
						action: {
							key: "download",
							function: () => {},
						},
						disabled: true,
					},
					{
						label: "common:new analysis",
						action: {
							key: "add",
							function: newAnalysisRedirect,
						},
					},
				],
			};
		}

		case PageType.WhatIfCarbonSnap: {
			if (!context) return undefined;

			const { onBackToList, onCalculate, calculateDisabled } =
				context as HeaderPageContextMap[PageType.WhatIfCarbonSnap];

			return {
				pageActions: [
					{
						label: "whatIf.carbonsnap:page/back to list",
						action: {
							key: "backToList",
							function: onBackToList,
						},
						disabled: true,
					},
					{
						label: "whatIf.carbonsnap:page/calculate results",
						action: {
							key: "calculate",
							function: onCalculate,
						},
						disabled: calculateDisabled,
					},
				],
			};
		}

		default:
			return undefined;
	}
}
